/*
  Needs details (idea 9) — tasks with no project, no person and no due date (the old "Untitled task"s and
  quick tests) wait here under the board instead of filling lanes. Check boxes + Select all; the navy bulk
  bar assigns / sets a project / a due date / moves them, or deletes them (after a confirmation).
  "Fill in one by one" opens the first in the task pane; the pane offers "Next ›" until the list is done.
  `readOnly` (BUG-002: Tasks is View only for the role, or a Viewer account): the list only — no "Fill in one by
  one", no check boxes, no bulk bar, no row ⋯ menu; a name still opens the task (read-only).
*/
import { useEffect, useMemo, useState } from 'react';
import { Inbox, PanelRightOpen, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatTimeAgo } from '@/lib/format';
import { RowActions } from '@/components/ui/row-actions';
import { StatusPill } from '@/components/ui/status-pill';
import { TableCheckbox } from '@/components/ui/table-checkbox';
import TaskBulkBar from './TaskBulkBar';
import { NO_SECTION, sectionOf } from './taskModel';

export default function NeedsDetailsCard({
  tasks,
  selectedTaskId,
  members,
  projects,
  sections,
  onOpen,
  onFillIn,
  onDelete,
  onBulkUpdate,
  onBulkDelete,
  readOnly = false,
}) {
  const [selected, setSelected] = useState([]);
  const ids = useMemo(() => tasks.map((task) => task.id), [tasks]);
  // A task that got its details (or was deleted) leaves the card — and the selection.
  useEffect(() => {
    setSelected((current) => {
      const next = current.filter((id) => ids.includes(id));
      return next.length === current.length ? current : next;
    });
  }, [ids]);

  if (!tasks.length) return null;
  const selectedTasks = tasks.filter((task) => selected.includes(task.id));
  const all = selected.length === tasks.length;
  const some = selected.length > 0 && !all;
  const toggle = (id) => setSelected((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));

  return (
    <section className="rounded-card border border-line bg-surface shadow-card" aria-labelledby="tasks-needs-details-title" data-testid="tasks-needs-details">
      <header className="flex flex-wrap items-start gap-3 border-b border-line-2 px-5 py-4">
        <span className="ui-icon-tile tone-amber h-9 w-9" aria-hidden="true"><Inbox className="h-[17px] w-[17px]" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 id="tasks-needs-details-title" className="text-[15px] font-bold text-ink">Needs details</h2>
            <StatusPill tone="amber" hideDot label={String(tasks.length)} size="sm" />
          </div>
          <p className="mt-0.5 text-[12.5px] text-ink-3">
            {readOnly ? 'No project, person or due date yet.' : 'No project, person or due date — fill them in, or clear out old tests.'}
          </p>
        </div>
        {readOnly ? null : (
          <button
            type="button"
            onClick={() => onFillIn(tasks[0])}
            className="mt-1 rounded text-[12.5px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="tasks-needs-details-fill"
          >
            Fill in one by one
          </button>
        )}
      </header>
      {readOnly ? null : (
        <label className="flex h-9 cursor-pointer items-center gap-3 border-b border-line-2 px-5 text-[12.5px] font-semibold text-ink-2">
          <TableCheckbox
            checked={all}
            indeterminate={some}
            onCheckedChange={(next) => setSelected(next ? ids : [])}
            aria-label={`Select all ${tasks.length} tasks that need details`}
            data-testid="tasks-needs-details-select-all"
          />
          Select all
        </label>
      )}
      <ul className="divide-y divide-[color:var(--line-2)]" aria-label="Tasks that need details">
        {tasks.map((task) => {
          const checked = selected.includes(task.id);
          const section = sectionOf(task);
          return (
            <li
              key={task.id}
              className={cn(
                'group flex min-h-[44px] items-center gap-3 px-5 py-1.5 transition-colors hover:bg-surface-hover',
                checked && 'bg-brand-soft hover:bg-brand-soft',
                selectedTaskId === task.id && 'shadow-[inset_3px_0_0_var(--brand)]'
              )}
              data-testid={`needs-details-row-${task.id}`}
            >
              {readOnly ? null : <TableCheckbox checked={checked} onCheckedChange={() => toggle(task.id)} aria-label={`Select ${task.name}`} />}
              <button
                type="button"
                onClick={() => onOpen(task)}
                dir="auto"
                className="min-w-0 truncate rounded text-start text-[13.5px] font-semibold text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {task.name}
              </button>
              <span className={cn('shrink-0 text-[12px] text-ink-3', section === NO_SECTION && 'italic')}>{section}</span>
              <span className="flex-1" />
              <span className="hidden shrink-0 text-[12px] text-ink-3 sm:inline" title={task.created_at || undefined}>
                {task.created_by ? `Created by ${task.created_by}` : 'Created'}
                {task.created_at ? ` · ${formatTimeAgo(task.created_at)}` : ''}
              </span>
              {readOnly ? null : (
                <RowActions
                  label={`Actions for ${task.name}`}
                  className="h-7 w-7"
                  items={[
                    { label: 'Open and fill in', icon: PanelRightOpen, onSelect: () => onOpen(task) },
                    { label: 'Delete task…', icon: Trash2, danger: true, onSelect: () => onDelete(task) },
                  ]}
                />
              )}
            </li>
          );
        })}
      </ul>
      {readOnly ? null : (
        <div className="px-3.5 pb-1 pt-0.5">
          <TaskBulkBar
            tasks={selectedTasks}
            members={members}
            projects={projects}
            sections={sections}
            onClear={() => setSelected([])}
            onBulkUpdate={onBulkUpdate}
            onBulkDelete={onBulkDelete}
            className="mt-2"
            testId="needs-details-bulk-bar"
          />
        </div>
      )}
    </section>
  );
}
